import { useEffect } from "react";
import type { SessionUser } from "../../shared/auth/access";
import { can } from "../../shared/auth/access";
import { WeeklyPlanningView } from "./WeeklyPlanningView";
import { useWeeklyPlanningWorkspace } from "./useWeeklyPlanningWorkspace";
import "./weekly-planning.css";

type WeeklyPlanningModuleProps = {
  user: SessionUser;
  dataRefreshVersion: number;
  onDirtyChange: (dirty: boolean) => void;
  initialWeekStart?: string | null;
  onWeekStartChange?: (weekStart: string) => void;
};

export function WeeklyPlanningModule({ user, dataRefreshVersion, onDirtyChange, initialWeekStart, onWeekStartChange }: WeeklyPlanningModuleProps) {
  const canEdit = can(user, "production.plan.write");
  const canActivate = can(user, "production.plan.activate");
  const workspace = useWeeklyPlanningWorkspace(canEdit, dataRefreshVersion, canActivate, initialWeekStart);

  useEffect(() => {
    onDirtyChange(workspace.dirty);
    return () => onDirtyChange(false);
  }, [onDirtyChange, workspace.dirty]);

  return <WeeklyPlanningView
    plan={workspace.plan}
    lines={workspace.lines}
    carryovers={workspace.carryovers}
    selectedCarryoverIds={workspace.selectedCarryoverIds}
    weekStart={workspace.weekStart}
    canEdit={canEdit}
    canActivate={canActivate}
    loading={workspace.loading}
    saving={workspace.saving}
    activating={workspace.activating}
    importing={workspace.importing}
    verifyingRowId={workspace.verifyingRowId}
    error={workspace.error}
    notice={workspace.notice}
    dirty={workspace.dirty}
    activationConfirmationOpen={workspace.activationConfirmationOpen}
    activationPreview={workspace.activationPreview}
    excludedRowsAcknowledged={workspace.excludedRowsAcknowledged}
    onWeekStartChange={(weekStart) => {
      workspace.setWeekStart(weekStart);
      onWeekStartChange?.(weekStart);
    }}
    onCreate={() => void workspace.create()}
    onImportFile={(file) => void workspace.importFile(file)}
    onTitleChange={workspace.title}
    onNoteChange={workspace.note}
    onDayStartsAtChange={workspace.dayStartsAt}
    onExecutionModeChange={workspace.executionMode}
    onAddProduction={() => workspace.addRow("PRODUCTION")}
    onAddActivity={() => workspace.addRow("ACTIVITY")}
    onUpdateRow={workspace.updateRow}
    onRemoveRow={workspace.removeRow}
    onMoveRow={workspace.moveRow}
    onToggleCarryover={workspace.toggleCarryover}
    onSave={() => void workspace.save()}
    onDiscard={() => void workspace.discard()}
    onOpenActivation={() => void workspace.openActivation()}
    onVerifyWorkOrder={(rowId, identity) => void workspace.verifyWorkOrder(rowId, identity)}
    onCancelActivation={workspace.cancelActivation}
    onActivate={() => void workspace.activate()}
    onExcludedRowsAcknowledgedChange={workspace.setExcludedRowsAcknowledged}
    onRetry={workspace.retry}
  />;
}
